import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight, Factory, Award, Globe2, Wrench } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import { fetchProducts } from "../lib/api";
import { SITE } from "../data/site";

export default function HomePage() {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    fetchProducts().then(setProducts).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page" className="bg-[var(--bg-primary)]">
      <Navbar variant="dark" />

      {/* HERO */}
      <section data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden bg-[var(--maroon-950)]">
        <video
          data-testid="hero-video"
          className="absolute inset-0 w-full h-full object-cover opacity-55"
          src="/media/pew-v1.mp4"
          autoPlay
          muted
          loop
          playsInline
          poster="/media/pew-1.jpeg"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--maroon-950)]/60 via-[var(--maroon-950)]/30 to-[var(--maroon-950)]/85" />
        <div className="absolute inset-0 grain pointer-events-none" />

        <div className="container-edge relative z-10 flex flex-col justify-end min-h-[100svh] pb-20 pt-32">
          <div className="max-w-4xl reveal">
            <div className="overline text-[#E8DCC8]">{SITE.short} · Patiala, Punjab · {SITE.yearsLegacy} Years</div>
            <h1 className="font-display text-white text-[44px] sm:text-6xl lg:text-7xl leading-[0.95] mt-5 tracking-tight">
              Precision dies <em className="not-italic text-[#E8DCC8]">&</em> rollers <br className="hidden sm:block" />
              for the world's biscuit lines.
            </h1>
            <p className="mt-7 text-[#E8DCC8]/85 text-lg max-w-2xl leading-relaxed">
              Every cookie, every cracker, every dairy biscuit on a moving belt starts with a die.
              We hand-engineer them in Patiala — for bakeries that refuse to compromise.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/products" className="btn-primary" data-testid="hero-cta-products">
                Explore Catalog <ArrowRight size={16} />
              </Link>
              <Link to="/contact" className="btn-ghost-light" data-testid="hero-cta-quote">
                Request a Quote
              </Link>
            </div>
          </div>

          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/15 border border-white/15 max-w-4xl">
            {SITE.facts.map((f) => (
              <div key={f.k} className="bg-[var(--maroon-950)] px-5 py-5">
                <div className="font-display text-2xl md:text-3xl text-white">{f.v}</div>
                <div className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-[#E8DCC8]/70 mt-1">{f.k}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-[var(--border-subtle)] bg-[var(--bg-secondary)] py-5 overflow-hidden">
        <div className="marquee-track">
          {[...SITE.industries, ...SITE.industries, ...SITE.industries].map((i, idx) => (
            <div key={idx} className="flex items-center gap-12">
              <span className="font-display text-2xl md:text-3xl text-[var(--maroon-900)] italic">{i}</span>
              <span className="text-[var(--maroon-700)]">✦</span>
            </div>
          ))}
        </div>
      </div>

      {/* INTRO / ABOUT SNIPPET */}
      <section className="section">
        <div className="container-edge grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5">
            <div className="overline">Workshop note</div>
            <h2 className="font-display text-4xl md:text-5xl mt-5 text-[var(--maroon-950)] leading-tight">
              A small workshop with a long memory.
            </h2>
          </div>
          <div className="md:col-span-7 md:pt-3">
            <p className="text-lg text-[var(--text-secondary)] leading-relaxed">
              Patiala Engineering Works has been making biscuit dies and bakery tooling out of Patiala, Punjab for over fifteen years.
              Under the stewardship of <strong className="text-[var(--maroon-900)]">{SITE.ceo}</strong>, our shop combines old-world precision metalwork
              with CNC engraving to ship dies that last longer, run cooler and cut sharper.
            </p>
            <p className="mt-5 text-lg text-[var(--text-secondary)] leading-relaxed">
              From the smallest custom cashew-cookie die to full sets of moulding and cutting rollers — our work moves with India's
              biscuit industry, and increasingly, beyond it.
            </p>
            <Link to="/about" className="btn-secondary mt-8" data-testid="home-about-cta">
              Read our story <ChevronRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* TRUSTED ON THE LINE */}
      <section className="section bg-[var(--bg-secondary)] relative grain">
        <div className="container-edge relative grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center">
          <div className="md:col-span-6">
            <img
              src="/media/cat-2.jpg"
              alt="Production biscuit line running with PEW tooling"
              className="w-full aspect-[4/3] object-cover border border-[var(--border-subtle)]"
              loading="lazy"
            />
          </div>
          <div className="md:col-span-6">
            <div className="overline">On the line</div>
            <h2 className="font-display text-4xl md:text-5xl mt-5 text-[var(--maroon-950)] leading-tight">
              Tooling that disappears into the production line — the way it should.
            </h2>
            <p className="mt-6 text-[var(--text-secondary)] leading-relaxed text-lg">
              The best dies are the ones the line manager forgets about. Ours are spec'd for long, hot shifts —
              dimensionally stable, drop-in compatible with major OEM rotary moulders and crackers, and finished to a
              standard that keeps reject rates low and shelf imprint sharp.
            </p>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                "Drop-in replacements",
                "Tight tolerance hold",
                "Hardened wear surfaces",
                "Custom shape engraving",
              ].map((f) => (
                <li key={f} className="flex items-start gap-3 text-[var(--text-primary)]">
                  <span className="mt-1.5 w-1.5 h-1.5 bg-[var(--maroon-700)] shrink-0" /> {f}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="section relative">
        <div className="container-edge relative">
          <div className="max-w-3xl">
            <div className="overline">What we do</div>
            <h2 className="font-display text-4xl md:text-5xl mt-5 text-[var(--maroon-950)] leading-tight">
              Tooling for the lines that feed a country.
            </h2>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-px bg-[var(--border-subtle)] border border-[var(--border-subtle)]">
            {[
              { icon: Wrench, title: "Custom Die Engraving", text: "CNC and hand-finished cavities for your unique biscuit shape, logo and weight." },
              { icon: Factory, title: "Moulding & Cutting Rollers", text: "Heavy-duty rollers designed to drop into existing rotary and cracker lines." },
              { icon: Award, title: "Built to Last", text: "Hardened, food-grade tooling that holds tolerance through long production runs." },
              { icon: Globe2, title: "Export Ready", text: "IEC certified — supplying bakeries and dairies in India and overseas." },
            ].map((c) => (
              <div key={c.title} className="bg-[var(--bg-primary)] p-7">
                <c.icon size={28} className="text-[var(--maroon-700)]" strokeWidth={1.5} />
                <h3 className="font-display text-xl mt-5 text-[var(--maroon-950)]">{c.title}</h3>
                <p className="mt-3 text-[var(--text-secondary)] text-[15px] leading-relaxed">{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="section">
        <div className="container-edge">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <div className="overline">The catalog</div>
              <h2 className="font-display text-4xl md:text-5xl mt-5 text-[var(--maroon-950)] leading-tight max-w-2xl">
                Six families of tooling. Endless variations.
              </h2>
            </div>
            <Link to="/products" className="btn-secondary" data-testid="home-products-cta">
              View full catalog <ChevronRight size={16} />
            </Link>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
            {products.slice(0, 6).map((p, i) => (
              <ProductCard key={p.slug} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS VIDEO */}
      <section className="section bg-[var(--maroon-950)] text-white relative grain overflow-hidden">
        <div className="container-edge relative grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <div className="overline text-[#D38888]">Inside the workshop</div>
            <h2 className="font-display text-4xl md:text-5xl mt-5 leading-tight">
              Steel, sparks, and a steady hand.
            </h2>
            <p className="mt-6 text-[#E8DCC8]/85 leading-relaxed">
              Every die that leaves our floor passes a sequence of CNC engraving, hand-finishing and run testing. Watch a small
              window into how we work — and the kind of detail we obsess over.
            </p>
            <Link to="/gallery" className="btn-ghost-light mt-8" data-testid="home-gallery-cta">
              See the gallery
            </Link>
          </div>
          <div className="md:col-span-7">
            <div className="aspect-video bg-black/40 border border-white/10 overflow-hidden">
              <video
                data-testid="home-process-video"
                className="w-full h-full object-cover"
                src="/media/pew-v2.mp4"
                autoPlay
                muted
                loop
                playsInline
                poster="/media/cat-3.jpg"
              />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
